import { useState } from "react";
import { ShieldCheck, ScanLine, Loader2, KeyRound, RotateCcw } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const SecurityDialog = ({ trigger }) => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | enroll
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");

  const reset = () => {
    setPhase("idle"); setData(null); setCode(""); setLoading(false);
  };

  const startReset = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/2fa/reset");
      setData(res.data);
      setPhase("enroll");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    setLoading(true);
    try {
      await api.post("/auth/2fa/confirm", { code });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("2FA re-enrolled successfully");
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-card border-border text-foreground max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-400" /> Security · Two-Factor Auth
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Manage your authenticator app and backup codes.
          </DialogDescription>
        </DialogHeader>

        {phase === "idle" && (
          <div className="space-y-4 py-1" data-testid="security-idle">
            <div className="flex items-center justify-between rounded-lg bg-muted/40 border border-border p-3">
              <span className="text-sm text-foreground">Current status</span>
              <span className={`text-xs font-medium px-2 py-1 rounded-full border ${user.totp_enabled
                ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/30"
                : "text-amber-300 bg-amber-500/15 border-amber-500/30"}`}>
                {user.totp_enabled ? "Enabled" : "Reset pending"}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Resetting will disable your current authenticator and generate a fresh QR code and
              new backup codes. You'll re-scan and confirm — 2FA stays required on your account.
            </p>
            <Button data-testid="reset-2fa-button" onClick={startReset} disabled={loading}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="h-4 w-4 mr-2" /> Reset 2FA</>}
            </Button>
          </div>
        )}

        {phase === "enroll" && data && (
          <div className="space-y-4 py-1" data-testid="security-enroll">
            <div className="flex items-center gap-2">
              <ScanLine className="h-4 w-4 text-indigo-400" />
              <p className="text-sm text-foreground">Scan with your authenticator app</p>
            </div>
            <div className="flex justify-center">
              <img src={data.qr_code} alt="New 2FA QR" data-testid="reset-qr"
                className="h-40 w-40 rounded-lg border border-border bg-white p-2" />
            </div>
            <div className="rounded-lg bg-muted/40 border border-border p-3">
              <p className="text-xs text-muted-foreground mb-1">Manual key</p>
              <code data-testid="reset-secret" className="text-xs text-indigo-300 break-all">{data.secret}</code>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><KeyRound className="h-3 w-3" /> Backup codes (save these)</p>
              <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-muted/40 border border-border p-3">
                {data.backup_codes.map((c) => (
                  <code key={c} data-testid="reset-backup-code" className="text-xs text-emerald-300 font-mono text-center">{c}</code>
                ))}
              </div>
              <Button variant="outline" data-testid="copy-reset-backup"
                onClick={() => { navigator.clipboard.writeText(data.backup_codes.join("\n")); toast.success("Backup codes copied"); }}
                className="w-full mt-2 border-border bg-card text-foreground hover:bg-accent">
                Copy backup codes
              </Button>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Enter 6-digit code to confirm</Label>
              <Input data-testid="reset-confirm-code" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" placeholder="000000"
                className="text-center text-xl tracking-[0.4em] font-mono bg-muted/40 border-border" />
            </div>
            <Button data-testid="reset-confirm-button" onClick={confirm} disabled={loading || code.length !== 6}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm re-enrollment"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
